import React, { useRef } from 'react';
import {
  FormControl,
  FormLabel,
  Input,
  InputProps,
  forwardRef,
  FormHelperText,
  FormErrorMessage,
  InputGroup,
  InputRightElement,
  InputLeftElement,
  InputElementProps,
} from '@chakra-ui/react';
import {
  FormErrorMessagePropsWithTestId,
  FormLabelPropsWithTestId,
  HelpTextPropsWithTestId,
} from 'types/general.types';
import clsx from 'clsx';
import { disableScrollingOnce } from 'app/utils/utils';

/**
 * By default input props that you pass to this component will
 * be  passed into a child `<Input>` component. If you want
 * to customize outer shell of this component or label, helper
 * text & error label - use `formControlProps`, `labelProps`,
 * `helperTextProps` and `errorLabelProps` respectively.
 */
export interface InputFieldProps extends InputProps {
  label?: string;
  placeholder?: string;
  helperText?: string;
  errorMessage?: React.ReactNode;

  labelProps?: FormLabelPropsWithTestId;
  helperTextProps?: HelpTextPropsWithTestId;
  errorMessageProps?: FormErrorMessagePropsWithTestId;

  leftElement?: JSX.Element;
  rightElement?: JSX.Element;
  rightElementProps?: Partial<InputElementProps>;
}

export const InputField = forwardRef<InputFieldProps, 'input'>((props, ref) => {
  const {
    label,
    placeholder,
    helperText,
    errorMessage,
    labelProps,
    helperTextProps,
    errorMessageProps,
    leftElement,
    rightElement,
    rightElementProps,
    // Following props are destructured because they are required
    // on FormControl element, and not on the Input element.
    id,
    isRequired,
    isDisabled,
    isInvalid,
    isReadOnly,
    ...rest
  } = props;

  const isDragging = useRef<boolean>(false);
  const inputElement = (
    <Input
      placeholder={placeholder || ' '}
      data-testid={placeholder || label || rest.name}
      // Prevent scroll into input on touch
      onTouchStart={(e) => {
        e.preventDefault();
        isDragging.current = false;
        Object.assign(e.currentTarget.readOnly, true);
        disableScrollingOnce();
      }}
      onTouchMove={() => {
        isDragging.current = true;
      }}
      onTouchEnd={(e) => {
        e.preventDefault();
        Object.assign(e.currentTarget.readOnly, false);
        if (!isDragging.current) {
          e.currentTarget.focus({ preventScroll: true });
        }
      }}
      isDisabled={isDisabled}
      {...rest}
      ref={ref}
      className={clsx(rest?.className, {
        hasLabel: Boolean(label),
      })}
    />
  );

  const inputLeftElement = leftElement && (
    <InputLeftElement sx={label ? { marginTop: 1 } : {}}>
      {leftElement}
    </InputLeftElement>
  );

  const inputRightElement = rightElement && (
    <InputRightElement {...rightElementProps}>{rightElement}</InputRightElement>
  );

  return (
    <FormControl
      variant="floating"
      {...{ id, isRequired, isDisabled, isInvalid, isReadOnly }}
    >
      {rightElement || leftElement ? (
        <InputGroup>
          {inputLeftElement}
          {inputElement}
          {inputRightElement}

          {/* Putting label here due to currently-impossible css - need :has to work. */}
          <FormLabel
            {...labelProps}
            className={clsx(labelProps?.className, {
              hasPlaceholder: Boolean(placeholder),
            })}
          >
            {label}
          </FormLabel>
        </InputGroup>
      ) : (
        <>
          {inputElement}
          <FormLabel
            {...labelProps}
            className={clsx(labelProps?.className, {
              hasPlaceholder: Boolean(placeholder),
              hasLabel: Boolean(label),
            })}
          >
            {label}
          </FormLabel>
        </>
      )}

      {helperText && !errorMessage && (
        <FormHelperText {...helperTextProps}>{helperText}</FormHelperText>
      )}

      {errorMessage && (
        <FormErrorMessage data-testid="error-text" {...errorMessageProps}>
          {errorMessage}
        </FormErrorMessage>
      )}
    </FormControl>
  );
});
